*** Begin Replacement
<template>
  <div class="p-6 bg-gray-50 min-h-screen">
    <!-- Filters -->
    <div class="flex flex-wrap justify-between items-center mb-6 gap-3">
      <div class="flex flex-wrap items-center gap-2">
        <input v-model="search" placeholder="Search equipment..." class="p-2 border rounded-lg" />
        <input type="date" v-model="startDate" class="p-2 border rounded-lg" />
        <input type="date" v-model="endDate" class="p-2 border rounded-lg" />
        <button
          @click="applyFilters"
          class="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Apply
        </button>
      </div>
      <div>
        <button
          @click="$emit('create')"
          class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          Add Usage
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-8 text-gray-600 text-lg font-medium">Loading...</div>

    <div v-else class="overflow-x-auto bg-white shadow rounded-xl">
      <table class="min-w-full border-collapse">
        <thead class="bg-gray-100">
          <tr>
            <th class="px-4 py-3 border text-left font-semibold">Equipment</th>
            <th class="px-4 py-3 border text-left font-semibold">Hours</th>
            <th class="px-4 py-3 border text-left font-semibold">Fuel</th>
            <th class="px-4 py-3 border text-left font-semibold">Date</th>
            <th class="px-4 py-3 border text-left font-semibold">Tasks</th>
            <th class="px-4 py-3 border text-left font-semibold">Created At</th>
            <th class="px-4 py-3 border text-left font-semibold">Actions</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="log in paginated" :key="log.id" class="hover:bg-gray-50 transition">
            <td class="px-4 py-3 border">
              {{ log.equipment?.name || getEquipmentName(log.equipment_id) }}
            </td>
            <td class="px-4 py-3 border">{{ log.hours_used ?? '-' }}</td>
            <td class="px-4 py-3 border">
              {{ getFuelDisplay(log) }}
            </td>
            <td class="px-4 py-3 border">{{ new Date(log.log_date).toLocaleDateString() }}</td>
            <td class="px-4 py-3 border">{{ log.tasks_performed ?? '-' }}</td>
            <td class="px-4 py-3 border">
              {{ log.created_at ? new Date(log.created_at).toLocaleString() : '-' }}
            </td>
            <td class="px-4 py-3 border flex gap-3">
              <button @click="$emit('view', log.id)" class="text-blue-600 hover:underline">
                View
              </button>
              <button @click="$emit('edit', log.id)" class="text-green-600 hover:underline">
                Edit
              </button>
              <button @click="$emit('delete', log.id)" class="text-red-600 hover:underline">
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="flex justify-between items-center mt-4">
      <div class="text-gray-700">Showing {{ paginated.length }} of {{ filtered.length }}</div>
      <div class="flex gap-2">
        <button
          @click="prev"
          :disabled="page === 1"
          class="px-3 py-2 bg-gray-200 rounded-lg disabled:opacity-50"
        >
          Prev
        </button>
        <button
          @click="next"
          :disabled="page >= totalPages"
          class="px-3 py-2 bg-gray-200 rounded-lg disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useUsageStore } from '@/stores/usageStore'
import { useEquipmentStore } from '@/stores/equipmentStore'
import type { UsageLog } from '@/types/usage'
import type { Equipment } from '@/types/equipment'

const usageStore = useUsageStore()
const equipmentStore = useEquipmentStore()

const loading = ref(false)
const page = ref(1)
const perPage = ref(12)
const search = ref('')
const startDate = ref('')
const endDate = ref('')

onMounted(async () => {
  loading.value = true
  try {
    await equipmentStore.fetchEquipment()
    await usageStore.fetchUsageLogs()
  } finally {
    loading.value = false
  }
})

const logs = computed(() => usageStore.usageLogs)

const filtered = computed(() => {
  let list = Array.isArray(logs.value) ? logs.value.slice() : []

  if (search.value) {
    const q = search.value.toLowerCase()
    list = list.filter((l) => (l.equipment?.name || '').toLowerCase().includes(q))
  }

  if (startDate.value) {
    const s = new Date(startDate.value)
    list = list.filter((l) => new Date(l.log_date) >= s)
  }

  if (endDate.value) {
    const e = new Date(endDate.value)
    list = list.filter((l) => new Date(l.log_date) <= e)
  }

  return list
})

const totalPages = computed(() => Math.max(1, Math.ceil(filtered.value.length / perPage.value)))

const paginated = computed(() => {
  const start = (page.value - 1) * perPage.value
  return filtered.value.slice(start, start + perPage.value)
})

function prev() {
  if (page.value > 1) page.value--
}

function next() {
  if (page.value < totalPages.value) page.value++
}

function applyFilters() {
  page.value = 1
}

function getEquipmentName(id: number | string | undefined) {
  // Handle null/undefined early
  if (id === undefined || id === null) return 'Unknown'

  const nid = Number(id)
  if (Number.isNaN(nid)) return 'Unknown'

  // Prefer explicit name cache if available
  const cached = equipmentStore.nameCache?.[nid]
  if (cached) return cached

  // Try to find in the loaded equipment list
  const found = equipmentStore.equipment.find((e) => Number(e.id) === nid)
  if (found) return found.name

  // Trigger an async fetch to populate cache, don't await here
  equipmentStore.fetchNameIfMissing(nid).catch(() => {})

  // Fallback to store helper which itself will return 'Unknown' if not found
  return equipmentStore.getName(nid)
}

function getFuelDisplay(log: UsageLog) {
  // If the log explicitly provides fuel_used, show it
  if (log.fuel_used !== undefined && log.fuel_used !== null) return String(log.fuel_used)

  // Otherwise, check whether the equipment requires fuel
  const eq = (log.equipment ??
    equipmentStore.equipment.find((e) => Number(e.id) === Number(log.equipment_id))) as
    | Equipment
    | undefined
  if (eq && eq.requires_fuel) return '-' // no value supplied yet

  // Not a fuel-using equipment
  return '—'
}
</script>

*** End Replacement
